import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import devportalApi from '../../../utils/devportalApi';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../../contexts/UserStoreContext';

const BillingSection = () => {
  const navigate = useNavigate();
  const { invoices, loading, error, fetchInvoices, payInvoice, getInvoiceStatus } = useUserStore();
  const [billingPeriod, setBillingPeriod] = useState('current');
  const [currentPlanDetails, setCurrentPlanDetails] = useState(null);
  const [plans, setPlans] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  // Fetch invoices and user data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userData, plansData] = await Promise.all([
          devportalApi.getUserInfo(),
          devportalApi.getPlans()
        ]);

        setCurrentPlanDetails(userData?.current_plan);
        setPlans(plansData);
        fetchInvoices();
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchData();
  }, [fetchInvoices]);

  // Get current usage data from real API data
  const getCurrentUsage = () => {
    if (!currentPlanDetails) {
      // Fallback to default values if no data
      return {
        plan: "Free",
        billingCycle: "Monthly",
        nextBilling: "N/A",
        amount: 0,
        apiCalls: 0,
        apiLimit: 100,
        overageRate: 0.001,
        estimatedBill: 0
      };
    }

    const currentPlan = plans.find(p => p.id === currentPlanDetails.id);

    // Get price - handle different formats
    let price = 0;
    if (currentPlan?.name?.toLowerCase() === 'free plan') {
      price = 0; // Explicitly set price to 0 for Free Plan
    } else if (currentPlan && currentPlan.price) {
      price = currentPlan.price / 100; // Convert from cents to dollars
    } else if (currentPlanDetails.price) {
      price = currentPlanDetails.price / 100; // Convert from cents to dollars
    }

    const activeUntil = currentPlanDetails.ActiveUntil || currentPlanDetails.active_until || currentPlanDetails.activeUntil;

    return {
      plan: currentPlan?.name || currentPlanDetails.name || 'Free',
      billingCycle: "Monthly",
      nextBilling: activeUntil ? new Date(activeUntil).toLocaleDateString() : 'N/A',
      amount: price,
      apiCalls: currentPlanDetails.api_calls_used || 0,
      apiLimit: currentPlanDetails.api_calls_limit || 100,
      overageRate: 0.001,
      estimatedBill: price
    };
  };

  const currentUsage = getCurrentUsage();

  // Filter and Sort Invoices
  const filteredInvoices = invoices
    .filter(inv => filterStatus === 'all' || inv.status === filterStatus)
    .sort((a, b) => {
      if (sortBy === 'date') return new Date(b.issue_date || b.date) - new Date(a.issue_date || a.date);
      if (sortBy === 'amount') return b.amount - a.amount;
      if (sortBy === 'status') return a.status.localeCompare(b.status);
      return 0;
    });

  const usagePercentage = (currentUsage?.apiCalls / currentUsage?.apiLimit) * 100;
  const remainingCalls = currentUsage?.apiLimit - currentUsage?.apiCalls;

  const generateAndDownloadInvoice = (invoice) => {
    // Create HTML content for the invoice
    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${invoice.period}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; color: #333; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
          .company-info { text-align: left; margin-bottom: 20px; }
          .invoice-info { text-align: right; margin-bottom: 30px; }
          .invoice-details { margin: 20px 0; }
          .invoice-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          .invoice-table th, .invoice-table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
          .invoice-table th { background-color: #f5f5f5; font-weight: bold; }
          .total-row { font-weight: bold; background-color: #f9f9f9; }
          .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #666; }
          .status-badge { display: inline-block; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: bold; }
          .status-succeeded { background-color: #d4edda; color: #155724; }
          .status-pending { background-color: #fff3cd; color: #856404; }
          .status-failed { background-color: #f8d7da; color: #721c24; }
          .status-canceled { background-color: #e2e3e5; color: #383d41; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>INVOICE</h1>
        </div>
        
        <div class="company-info">
          <h2>MessagingHub Pro</h2>
          <p>Professional Messaging Services</p>
          <p>support@messaginghub.com</p>
        </div>
        
        <div class="invoice-info">
          <p><strong>Invoice Number:</strong> ${invoice.id}</p>
          <p><strong>Date:</strong> ${invoice.dateTime}</p>
          <p><strong>Status:</strong> <span class="status-badge status-${invoice.status}">${invoice.status.toUpperCase()}</span></p>
        </div>
        
        <div class="invoice-details">
          <table class="invoice-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Period</th>
                <th>Provider</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${invoice.period} Subscription</td>
                <td>${invoice.date}</td>
                <td>${invoice.provider.toUpperCase()}</td>
                <td>${invoice.currency} ${invoice.amount.toFixed(2)}</td>
              </tr>
              <tr class="total-row">
                <td colspan="3"><strong>Total Amount</strong></td>
                <td><strong>${invoice.currency} ${invoice.amount.toFixed(2)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="footer">
          <p>Thank you for your business!</p>
          <p>This is a computer-generated invoice and requires no signature.</p>
          <p>Generated on ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;

    // Create a new window and print the invoice
    const printWindow = window.open('', '_blank');
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();

    // Wait for the content to load, then print
    printWindow.onload = function () {
      printWindow.print();
      printWindow.close();
    };
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'succeeded':
        return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400';
      case 'pending':
      case 'sent':
        return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400';
      case 'overdue':
      case 'failed':
        return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400';
      case 'draft':
        return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-900/30 dark:text-gray-400';
      case 'canceled':
        return 'bg-muted/10 text-muted-foreground border-muted/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <div className="space-y-6">
      {/* Current Usage & Billing */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">Billing & Usage</h2>
          <Button
            variant="default"
            size="sm"
            iconName="TrendingUp"
            iconPosition="left"
            onClick={() => navigate('/developer-portal', { state: { activeTab: 'plans-billing' } })}
          >
            Upgrade Plan
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Current Plan */}
          <div className="p-4 bg-muted/30 rounded-lg border border-border">
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="Package" size={16} className="text-primary" />
              <span className="text-sm text-muted-foreground">Current Plan</span>
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-2">{currentUsage?.plan}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Billing Cycle:</span>
                <span className="text-foreground">{currentUsage?.billingCycle}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Next Billing:</span>
                <span className="text-foreground">{currentUsage?.nextBilling}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount:</span>
                <span className="text-foreground font-medium">${currentUsage?.amount?.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Usage Statistics */}
          <div className="p-4 bg-muted/30 rounded-lg border border-border">
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="Activity" size={16} className="text-secondary" />
              <span className="text-sm text-muted-foreground">API Usage</span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-2xl font-bold text-foreground">{currentUsage?.apiCalls?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">of {currentUsage?.apiLimit?.toLocaleString()} calls</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-foreground">{usagePercentage?.toFixed(1)}%</p>
                  <p className="text-xs text-muted-foreground">used</p>
                </div>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-secondary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${usagePercentage}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {remainingCalls?.toLocaleString()} calls remaining this cycle
              </p>
            </div>
          </div>
        </div>

        {/* Estimated Bill */}
        <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="DollarSign" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">Estimated Next Bill</span>
            </div>
            <div className="text-right">
              <p className="text-xl font-bold text-primary">${currentUsage?.estimatedBill?.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">Due {currentUsage?.nextBilling}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Invoice History */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 space-y-4 sm:space-y-0">
          <h2 className="text-xl font-semibold text-foreground">Invoice History</h2>
          <div className="flex items-center space-x-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-background border border-border rounded-md text-sm px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="canceled">Canceled</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-background border border-border rounded-md text-sm px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="date">Sort by Date</option>
              <option value="amount">Sort by Amount</option>
              <option value="status">Sort by Status</option>
            </select>
            <Button
              variant="ghost"
              size="sm"
              iconName="Download"
              iconPosition="left"
            >
              Download All
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2 text-muted-foreground">Loading payments history...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="AlertCircle" size={20} className="text-destructive mr-2" />
              <span className="text-destructive">Error loading payments: {error}</span>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="FileText" size={20} className="text-muted-foreground mr-2" />
              <span className="text-muted-foreground">No invoices found matching your filters</span>
            </div>
          ) : (
            filteredInvoices?.map((invoice) => (
              <div key={invoice?.id} className="bg-card border border-border rounded-xl p-6 hover:shadow-elevation-2 transition-all duration-200">
                <div className="flex items-start justify-between">
                  {/* Left Section - Invoice Info */}
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="p-3 bg-primary/10 rounded-xl">
                      <Icon name="FileText" size={20} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-foreground">{invoice?.invoice_number || invoice?.id?.substring(0, 8)}</h3>
                        <div className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(invoice?.status)}`}>
                          {invoice?.status}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground flex items-center space-x-2">
                          <Icon name="Calendar" size={14} className="text-muted-foreground" />
                          <span>{new Date(invoice?.issue_date || invoice?.date).toLocaleDateString()}</span>
                        </p>
                        {invoice?.description && (
                          <p className="text-sm text-muted-foreground truncate">
                            {invoice.description}
                          </p>
                        )}
                        <p className="text-sm font-medium text-primary flex items-center space-x-2">
                          <Icon name="CreditCard" size={14} />
                          <span>{invoice?.currency || 'USD'}</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Right Section - Amount & Actions */}
                  <div className="flex flex-col items-end space-y-3">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-foreground">{(invoice?.currency || '$')} {(invoice?.amount / 100).toFixed(2)}</p>
                    </div>
                    <div className="flex space-x-2">
                      {invoice.status !== 'paid' && invoice.status !== 'canceled' && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => navigate(`/invoices/${invoice.id}`)}
                          className="h-8 px-3"
                        >
                          Pay Now
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Download"
                        iconPosition="left"
                        onClick={() => {
                          generateAndDownloadInvoice(invoice);
                        }}
                        className="h-8 px-3"
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mt-6 pt-4 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            iconName="ChevronDown"
            iconPosition="right"
            className="w-full"
          >
            Load More Invoices
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BillingSection;
